import {
  NFL_STAT_GROUPS,
  getNflGridFields,
  getNflPlayerPath,
} from "../adapters/nflPlayerAdapter";
import { getActiveNflFranchises } from "../utils/nflFranchises";

const VALUE_FIELD = (f) => f.id !== "year" && f.id !== "team";

const fieldsByGroup = {
  passing: getNflGridFields("passing").filter(VALUE_FIELD).map((f) => f.id),
  rushing: getNflGridFields("rushing").filter(VALUE_FIELD).map((f) => f.id),
  receiving: getNflGridFields("receiving").filter(VALUE_FIELD).map((f) => f.id),
};

function buildFieldMeta(groupIds) {
  const meta = {};
  groupIds.forEach((groupId) => {
    getNflGridFields(groupId)
      .filter(VALUE_FIELD)
      .forEach((f) => {
        if (!meta[f.id]) {
          meta[f.id] = {
            label: f.label,
            abbrev: f.label,
            rate: f.format === "pct" || f.format === "decimal1",
            format: f.format,
          };
        }
      });
  });
  return meta;
}

const fieldMeta = buildFieldMeta(["passing", "rushing", "receiving"]);

const NFL_LANDING_PASSING = {
  queryType: "career",
  startYear: 1920,
  endYear: 2026,
  selectedStats: ["yds", "td", "int", "att"],
  statisticalFilters: { yds: { operator: ">=", value: 30000 } },
  sortBy: "yds",
  sortDirection: -1,
  limit: 50,
  groupId: "passing",
  teams: [],
  biographicalFilters: {},
};

const NFL_LANDING_RUSHING = {
  queryType: "career",
  startYear: 1920,
  endYear: 2026,
  selectedStats: ["yds", "td", "att"],
  statisticalFilters: { yds: { operator: ">=", value: 8000 } },
  sortBy: "yds",
  sortDirection: -1,
  limit: 50,
  groupId: "rushing",
  teams: [],
  biographicalFilters: {},
};

const NFL_LANDING_RECEIVING = {
  queryType: "career",
  startYear: 1920,
  endYear: 2026,
  selectedStats: ["rec", "yds", "td"],
  statisticalFilters: { yds: { operator: ">=", value: 10000 } },
  sortBy: "yds",
  sortDirection: -1,
  limit: 50,
  groupId: "receiving",
  teams: [],
  biographicalFilters: {},
};

const recommendedByGroup = {
  passing: [
    { id: "yds", stat: "yds", operator: ">=", careerValue: 30000, seasonValue: 3000, label: "Passing Yards", abbrev: "YDS" },
    { id: "td", stat: "td", operator: ">=", careerValue: 200, seasonValue: 25, label: "Passing TDs", abbrev: "TD" },
  ],
  rushing: [
    { id: "yds", stat: "yds", operator: ">=", careerValue: 8000, seasonValue: 800, label: "Rushing Yards", abbrev: "YDS" },
    { id: "td", stat: "td", operator: ">=", careerValue: 60, seasonValue: 10, label: "Rushing TDs", abbrev: "TD" },
  ],
  receiving: [
    { id: "rec", stat: "rec", operator: ">=", careerValue: 500, seasonValue: 80, label: "Receptions", abbrev: "REC" },
    { id: "yds", stat: "yds", operator: ">=", careerValue: 8000, seasonValue: 1000, label: "Receiving Yards", abbrev: "YDS" },
  ],
};

const passingPreset = ({ id, title, subtitle, category, featured = false, queryType = "career", stats, filters, sortBy }) => ({
  id,
  title,
  subtitle,
  category,
  featured,
  params: {
    ...NFL_LANDING_PASSING,
    groupId: "passing",
    queryType,
    selectedStats: stats,
    statisticalFilters: filters,
    sortBy,
    sortDirection: -1,
  },
});

const rushingPreset = ({ id, title, subtitle, category, featured = false, queryType = "career", stats, filters, sortBy }) => ({
  id,
  title,
  subtitle,
  category,
  featured,
  params: {
    ...NFL_LANDING_RUSHING,
    groupId: "rushing",
    queryType,
    selectedStats: stats,
    statisticalFilters: filters,
    sortBy,
    sortDirection: -1,
  },
});

const receivingPreset = ({ id, title, subtitle, category, featured = false, queryType = "career", stats, filters, sortBy }) => ({
  id,
  title,
  subtitle,
  category,
  featured,
  params: {
    ...NFL_LANDING_RECEIVING,
    groupId: "receiving",
    queryType,
    selectedStats: stats,
    statisticalFilters: filters,
    sortBy,
    sortDirection: -1,
  },
});

const presets = [
  passingPreset({
    id: "40k-pass-yds",
    title: "40,000+ Passing Yards",
    subtitle: "Elite career passers",
    category: "passing",
    stats: ["yds", "td", "int"],
    filters: { yds: { operator: ">=", value: 40000 } },
    sortBy: "yds",
  }),
  passingPreset({
    id: "50k-pass-yds",
    title: "50,000+ Passing Yards",
    subtitle: "All-time passing greats",
    category: "passing",
    featured: true,
    stats: ["yds", "td", "int"],
    filters: { yds: { operator: ">=", value: 50000 } },
    sortBy: "yds",
  }),
  passingPreset({
    id: "300-pass-tds",
    title: "300+ Passing TDs",
    subtitle: "Career touchdown milestones",
    category: "passing",
    stats: ["td", "yds"],
    filters: { td: { operator: ">=", value: 300 } },
    sortBy: "td",
  }),
  passingPreset({
    id: "4000-yard-passing-seasons",
    title: "4,000+ Yard Passing Seasons",
    subtitle: "Single seasons with elite yardage",
    category: "seasons",
    queryType: "season",
    stats: ["yds", "td"],
    filters: { yds: { operator: ">=", value: 4000 } },
    sortBy: "yds",
  }),
  rushingPreset({
    id: "10k-rush-yds",
    title: "10,000+ Rushing Yards",
    subtitle: "All-time rushing milestones",
    category: "rushing",
    featured: true,
    stats: ["yds", "td", "att"],
    filters: { yds: { operator: ">=", value: 10000 } },
    sortBy: "yds",
  }),
  rushingPreset({
    id: "12k-rush-yds",
    title: "12,000+ Rushing Yards",
    subtitle: "Elite career rushers",
    category: "rushing",
    stats: ["yds", "td", "att"],
    filters: { yds: { operator: ">=", value: 12000 } },
    sortBy: "yds",
  }),
  rushingPreset({
    id: "100-rush-tds",
    title: "100+ Rushing TDs",
    subtitle: "Career rushing touchdown leaders",
    category: "rushing",
    stats: ["td", "yds"],
    filters: { td: { operator: ">=", value: 100 } },
    sortBy: "td",
  }),
  rushingPreset({
    id: "1200-yard-rushing-seasons",
    title: "1,200+ Yard Rushing Seasons",
    subtitle: "Elite single-season rushing performances",
    category: "seasons",
    queryType: "season",
    stats: ["yds", "td"],
    filters: { yds: { operator: ">=", value: 1200 } },
    sortBy: "yds",
  }),
  receivingPreset({
    id: "10k-rec-yds",
    title: "10,000+ Receiving Yards",
    subtitle: "Career receiving milestones",
    category: "receiving",
    stats: ["yds", "rec", "td"],
    filters: { yds: { operator: ">=", value: 10000 } },
    sortBy: "yds",
  }),
  receivingPreset({
    id: "15k-rec-yds",
    title: "15,000+ Receiving Yards",
    subtitle: "All-time receiving greats",
    category: "receiving",
    stats: ["yds", "rec", "td"],
    filters: { yds: { operator: ">=", value: 15000 } },
    sortBy: "yds",
  }),
  receivingPreset({
    id: "1000-receptions",
    title: "1,000+ Receptions",
    subtitle: "Elite career reception leaders",
    category: "receiving",
    featured: true,
    stats: ["rec", "yds", "td"],
    filters: { rec: { operator: ">=", value: 1000 } },
    sortBy: "rec",
  }),
  receivingPreset({
    id: "100-rec-tds",
    title: "100+ Receiving TDs",
    subtitle: "Career receiving touchdown leaders",
    category: "receiving",
    stats: ["td", "yds", "rec"],
    filters: { td: { operator: ">=", value: 100 } },
    sortBy: "td",
  }),
  receivingPreset({
    id: "100-catch-seasons",
    title: "100+ Catch Seasons",
    subtitle: "Single seasons with 100+ receptions",
    category: "seasons",
    queryType: "season",
    stats: ["rec", "yds", "td"],
    filters: { rec: { operator: ">=", value: 100 } },
    sortBy: "rec",
  }),
];

const featuredPresets = presets.filter((p) => p.featured);

export const nflCatalog = {
  sport: "nfl",
  leagueScopeToggle: true,
  heroSubtitle: "Browse NFL careers and seasons with visual filters and shareable URLs.",
  queryTypes: [
    { value: "career", label: "Career" },
    { value: "season", label: "Season" },
  ],
  groups: NFL_STAT_GROUPS,
  defaultGroupId: "passing",
  yearMin: 1920,
  yearMax: 2026,
  nameSortField: "bioData.displayName",
  getPlayerPath: getNflPlayerPath,
  fieldsByGroup,
  fieldMeta,
  landingByGroup: {
    passing: NFL_LANDING_PASSING,
    rushing: NFL_LANDING_RUSHING,
    receiving: NFL_LANDING_RECEIVING,
  },
  recommendedByGroup,
  presets,
  featuredPresets,
  bioFields: [
    { value: "nameStartsWith", label: "Name starts with" },
    { value: "nameEndsWith", label: "Name ends with" },
    { value: "birthPlace", label: "Birth place" },
    { value: "position", label: "Position" },
  ],
  teams: getActiveNflFranchises(),
};
